import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { HeartHandshake, Send, Loader2, MessageCircle, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'work_life_balance', label: 'Work-Life Balance' },
  { value: 'team_dynamics', label: 'Team Dynamics' },
  { value: 'career_growth', label: 'Career Growth' },
  { value: 'personal', label: 'Personal' },
  { value: 'health', label: 'Health & Wellness' },
  { value: 'other', label: 'Other' },
];

const GopalPage = () => {
  const { user, isHR } = useAuth();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newConcern, setNewConcern] = useState('');
  const [category, setCategory] = useState('general');
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [adminStatus, setAdminStatus] = useState('');

  useEffect(() => { fetchConcerns(); }, []);

  const fetchConcerns = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gopal/concerns`, { withCredentials: true });
      setConcerns(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newConcern.trim()) { toast.error('Please describe your concern'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/gopal/concerns`, {
        concern: newConcern.trim(), category,
      }, { withCredentials: true });
      toast.success('Your concern has been submitted. A relevant team will reach out to you.');
      setNewConcern('');
      setCategory('general');
      fetchConcerns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminUpdate = async () => {
    if (!selectedConcern) return;
    try {
      await axios.put(`${API_URL}/api/gopal/concerns/${selectedConcern.id}`, {
        status: adminStatus || selectedConcern.status,
        admin_notes: adminNotes,
      }, { withCredentials: true });
      toast.success('Concern updated');
      setSelectedConcern(null);
      fetchConcerns();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
      in_progress: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertCircle },
      resolved: { cls: 'badge-success', icon: CheckCircle },
    };
    const c = config[status] || config.submitted;
    return <Badge className={`${c.cls} gap-1`}><c.icon className="w-3 h-3" /> {(status || 'submitted').replace('_', ' ')}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="gopal-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><HeartHandshake className="w-7 h-7 text-rose-500" /> Gopal</h1>
        <p className="text-muted-foreground">A safe space to share your concerns. A relevant team will reach out and help.</p>
      </div>

      {/* Submit new concern (for employees) */}
      {!isHR && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Share a Concern</CardTitle>
            <CardDescription>Your submission is confidential. A counselor or relevant team will reach out to you.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="concern-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>What's on your mind?</Label>
              <Textarea value={newConcern} onChange={(e) => setNewConcern(e.target.value)} rows={4} placeholder="Share your concern here. This is a safe, confidential space..." data-testid="concern-text" />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="gap-2" data-testid="submit-concern-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit
            </Button>
          </CardContent>
        </Card>
      )}

      {/* List of concerns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{isHR ? 'All Employee Concerns' : 'My Submissions'}</CardTitle>
          <CardDescription>{concerns.length} concern(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {concerns.length > 0 ? (
            <div className="space-y-3">
              {concerns.map(c => (
                <div key={c.id} className="p-4 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => { if (isHR) { setSelectedConcern(c); setAdminNotes(c.admin_notes || ''); setAdminStatus(c.status); } }} data-testid={`concern-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {isHR && <p className="text-sm font-semibold text-primary">{c.employee_name}</p>}
                      <p className="text-sm mt-1">{c.concern}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">{CATEGORIES.find(cat => cat.value === c.category)?.label || c.category}</Badge>
                        <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString('en-IN')}</span>
                      </div>
                      {c.admin_notes && (
                        <div className="mt-2 p-2 rounded bg-muted/50 text-sm">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Response:</p>
                          <p>{c.admin_notes}</p>
                        </div>
                      )}
                    </div>
                    {getStatusBadge(c.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">{isHR ? 'No employee concerns submitted yet.' : 'No concerns submitted. Your well-being matters to us.'}</p>
          )}
        </CardContent>
      </Card>

      {/* Admin response dialog */}
      <Dialog open={!!selectedConcern} onOpenChange={(open) => !open && setSelectedConcern(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to Concern</DialogTitle>
          </DialogHeader>
          {selectedConcern && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium">{selectedConcern.employee_name}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedConcern.concern}</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={adminStatus} onValueChange={setAdminStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Response Notes</Label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={3} placeholder="Add your response or notes..." data-testid="admin-notes" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedConcern(null)}>Cancel</Button>
                <Button onClick={handleAdminUpdate} data-testid="update-concern-btn">Update</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GopalPage;
